"use client";

import { useCallback, useEffect } from "react";
import { atom, useAtom } from "jotai";
import {
  ColorFormat,
  ColorItem,
  ContrastBadgeStatus,
  ContrastCell,
  ContrastMatrix,
  PaletteShareState,
  SelectedPair,
} from "@constructr-sdk/entities";
import type {
  UseColorExplorerParams,
  UseColorExplorerReturn,
} from "~/modules/main/hooks/use-color-explorer/use-color-explorer.interface";
import { generateUUID } from "@constructr-sdk/core/lib/utils";

// Global state atoms - hoisted to top level
const colorsAtom = atom<ColorItem[]>([]);
const selectedPairAtom = atom<SelectedPair | null>(null);
const contrastMatrixAtom = atom<ContrastMatrix | null>(null);
const shareAtom = atom<PaletteShareState | null>(null);
const lastCopiedTextAtom = atom<string | null>(null);

// Color conversion utilities
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1]!, 16),
        g: parseInt(result[2]!, 16),
        b: parseInt(result[3]!, 16),
      }
    : null;
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

function parseRgb(value: string): { r: number; g: number; b: number } | null {
  const match = value.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
  if (!match) return null;
  return {
    r: parseInt(match[1]!, 10),
    g: parseInt(match[2]!, 10),
    b: parseInt(match[3]!, 10),
  };
}

function parseHsl(value: string): { h: number; s: number; l: number } | null {
  const match = value.match(
    /hsl\s*\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/i,
  );
  if (!match) return null;
  return {
    h: parseInt(match[1]!, 10),
    s: parseInt(match[2]!, 10),
    l: parseInt(match[3]!, 10),
  };
}

function validateHex(value: string): boolean {
  return /^#?[0-9A-Fa-f]{6}$/.test(value);
}

function validateRgb(value: string): boolean {
  const rgb = parseRgb(value);
  if (!rgb) return false;
  return (
    rgb.r >= 0 && rgb.r <= 255 && rgb.g >= 0 && rgb.g <= 255 && rgb.b >= 0 && rgb.b <= 255
  );
}

function validateHsl(value: string): boolean {
  const hsl = parseHsl(value);
  if (!hsl) return false;
  return (
    hsl.h >= 0 && hsl.h <= 360 && hsl.s >= 0 && hsl.s <= 100 && hsl.l >= 0 && hsl.l <= 100
  );
}

function normalizeHex(value: string): string {
  return value.startsWith("#") ? value : "#" + value;
}

function convertToFormat(hex: string, format: ColorFormat): string {
  switch (format) {
    case ColorFormat.HEX:
      return hex.toUpperCase();
    case ColorFormat.RGB: {
      const rgb = hexToRgb(hex);
      if (!rgb) return hex;
      return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    }
    case ColorFormat.HSL: {
      const hsl = hexToHsl(hex);
      if (!hsl) return hex;
      return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
    }
    default:
      return hex;
  }
}

function parseColorValue(
  value: string,
  format: ColorFormat,
): { hex: string; isValid: boolean } {
  let hex = "";
  let isValid = false;

  switch (format) {
    case ColorFormat.HEX:
      isValid = validateHex(value);
      if (isValid) {
        hex = normalizeHex(value).toUpperCase();
      }
      break;
    case ColorFormat.RGB: {
      isValid = validateRgb(value);
      if (isValid) {
        const rgb = parseRgb(value);
        if (rgb) {
          hex = rgbToHex(rgb.r, rgb.g, rgb.b).toUpperCase();
        }
      }
      break;
    }
    case ColorFormat.HSL: {
      isValid = validateHsl(value);
      if (isValid) {
        const hsl = parseHsl(value);
        if (hsl) {
          hex = hslToHex(hsl.h, hsl.s, hsl.l).toUpperCase();
        }
      }
      break;
    }
  }

  return { hex, isValid };
}

// WCAG contrast calculation
function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((val) => {
    const normalized = val / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

function getContrastRatio(hex1: string, hex2: string): number {
  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

function getContrastStatus(ratio: number): ContrastBadgeStatus {
  if (ratio >= 7.0) return ContrastBadgeStatus.AAA;
  if (ratio >= 4.5) return ContrastBadgeStatus.AA;
  return ContrastBadgeStatus.FAIL;
}

// URL encoding/decoding
function encodePalette(colors: ColorItem[]): string {
  const data = colors.map((c) => ({
    hex: c.hex,
    format: c.format,
  }));
  return btoa(JSON.stringify(data));
}

function decodePalette(encoded: string): ColorItem[] {
  try {
    const data = JSON.parse(atob(encoded)) as Array<{
      hex: string;
      format: ColorFormat;
    }>;
    return data.map((item) => {
      const parsed = parseColorValue(item.hex, item.format);
      return {
        id: generateUUID(),
        value: convertToFormat(item.hex, item.format),
        format: item.format,
        hex: item.hex,
        isValid: parsed.isValid,
      };
    });
  } catch {
    return [];
  }
}

// Create initial default colors
function createDefaultColors(): ColorItem[] {
  return [
    {
      id: generateUUID(),
      value: "#111111",
      format: ColorFormat.HEX,
      hex: "#111111",
      isValid: true,
    },
    {
      id: generateUUID(),
      value: "#FFFFFF",
      format: ColorFormat.HEX,
      hex: "#FFFFFF",
      isValid: true,
    },
  ];
}

export function useColorExplorer(
  params: UseColorExplorerParams,
): UseColorExplorerReturn {
  const [colors, setColors] = useAtom(colorsAtom);
  const [selectedPair, setSelectedPair] = useAtom(selectedPairAtom);
  const [contrastMatrix, setContrastMatrix] = useAtom(contrastMatrixAtom);
  const [share, setShare] = useAtom(shareAtom);
  const [lastCopiedText, setLastCopiedText] = useAtom(lastCopiedTextAtom);

  // Initialize colors on mount
  useEffect(() => {
    if (colors.length === 0) {
      setColors(createDefaultColors());
    }
  }, [colors.length, setColors]);

  // Import from encoded palette if provided
  useEffect(() => {
    if (params.initialEncodedPalette) {
      const imported = decodePalette(params.initialEncodedPalette);
      if (imported.length >= 2 && imported.length <= 10) {
        setColors(imported);
        setSelectedPair(null);
        setContrastMatrix(null);
      }
    }
  }, [params.initialEncodedPalette, setColors, setSelectedPair, setContrastMatrix]);

  // Computed state
  const isAtLimit = colors.length >= 10;

  // Mutations
  const setColorValue = useCallback(
    (id: string, nextValue: string) => {
      setColors((prev) =>
        prev.map((color) => {
          if (color.id !== id) return color;
          const parsed = parseColorValue(nextValue, color.format);
          return {
            ...color,
            value: nextValue,
            hex: parsed.isValid ? parsed.hex : color.hex,
            isValid: parsed.isValid,
          };
        }),
      );
    },
    [setColors],
  );

  const setColorFormat = useCallback(
    (id: string, nextFormat: ColorFormat) => {
      setColors((prev) =>
        prev.map((color) => {
          if (color.id !== id) return color;
          const newValue = convertToFormat(color.hex, nextFormat);
          return {
            ...color,
            format: nextFormat,
            value: newValue,
          };
        }),
      );
    },
    [setColors],
  );

  const addColor = useCallback(
    (initialHex: string, format: ColorFormat) => {
      if (isAtLimit) return;
      const parsed = parseColorValue(initialHex, format);
      const newColor: ColorItem = {
        id: generateUUID(),
        value: parsed.isValid ? convertToFormat(parsed.hex, format) : initialHex,
        format,
        hex: parsed.isValid ? parsed.hex : "#000000",
        isValid: parsed.isValid,
      };
      setColors((prev) => [...prev, newColor]);
    },
    [isAtLimit, setColors],
  );

  const removeColor = useCallback(
    (id: string) => {
      setColors((prev) => prev.filter((c) => c.id !== id));
      setSelectedPair((prev) => {
        if (prev && (prev.bgId === id || prev.fgId === id)) {
          return null;
        }
        return prev;
      });
    },
    [setColors, setSelectedPair],
  );

  const selectPair = useCallback(
    (pair: SelectedPair | null) => {
      if (pair) {
        const bgExists = colors.some((c) => c.id === pair.bgId);
        const fgExists = colors.some((c) => c.id === pair.fgId);
        if (bgExists && fgExists) {
          setSelectedPair(pair);
        }
      } else {
        setSelectedPair(null);
      }
    },
    [colors, setSelectedPair],
  );

  // Commands
  const recomputeMatrix = useCallback(async () => {
    const validColors = colors.filter((c) => c.isValid);
    if (validColors.length < 2) {
      setContrastMatrix(null);
      return;
    }

    const cells: ContrastCell[] = [];
    const colorIds = validColors.map((c) => c.id);

    for (let i = 0; i < validColors.length; i++) {
      for (let j = 0; j < validColors.length; j++) {
        if (i === j) continue;
        const bg = validColors[i]!;
        const fg = validColors[j]!;
        const ratio = getContrastRatio(bg.hex, fg.hex);
        cells.push({
          bgId: bg.id,
          fgId: fg.id,
          ratio: Math.round(ratio * 100) / 100,
          status: getContrastStatus(ratio),
        });
      }
    }

    setContrastMatrix({
      colorIds,
      cells,
    });
  }, [colors, setContrastMatrix]);

  const resetPalette = useCallback(async () => {
    setColors(createDefaultColors());
    setSelectedPair(null);
    setContrastMatrix(null);
    setShare(null);
    setLastCopiedText(null);
  }, [setColors, setSelectedPair, setContrastMatrix, setShare, setLastCopiedText]);

  const generateShareUrl = useCallback(async () => {
    const encoded = encodePalette(colors);
    const baseUrl = process.env.NEXT_PUBLIC_APP_URL || window.location.origin;
    const url = `${baseUrl}/color-explorer?palette=${encoded}`;
    setShare({ url, encoded });
  }, [colors, setShare]);

  const copyColorValue = useCallback(
    async (id: string) => {
      const color = colors.find((c) => c.id === id);
      if (!color) return;
      try {
        await navigator.clipboard.writeText(color.value);
        setLastCopiedText(color.value);
      } catch (error) {
        console.error("Failed to copy color value:", error);
      }
    },
    [colors, setLastCopiedText],
  );

  const copyAllColors = useCallback(async () => {
    const allValues = colors.map((c) => c.value).join(", ");
    try {
      await navigator.clipboard.writeText(allValues);
      setLastCopiedText("All colors copied");
    } catch (error) {
      console.error("Failed to copy all colors:", error);
    }
  }, [colors, setLastCopiedText]);

  const importFromEncoded = useCallback(
    async (encoded: string) => {
      const imported = decodePalette(encoded);
      if (imported.length >= 2 && imported.length <= 10) {
        setColors(imported);
        setSelectedPair(null);
        setContrastMatrix(null);
        setShare(null);
      }
    },
    [setColors, setSelectedPair, setContrastMatrix, setShare],
  );

  return {
    // State
    colors,
    selectedPair,
    contrastMatrix,
    share,
    isAtLimit,
    lastCopiedText,

    // Mutations
    setColorValue,
    setColorFormat,
    addColor,
    removeColor,
    selectPair,

    // Commands
    recomputeMatrix,
    resetPalette,
    generateShareUrl,
    copyColorValue,
    copyAllColors,
    importFromEncoded,
  };
}